import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  useDisclosure,
  Flex,
  Badge,
  List,
  ListItem,
  ListIcon,
  Icon,
} from "@chakra-ui/react";
import { CheckCircleIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { FiPlay, FiHeadphones, FiDollarSign, FiUsers } from "react-icons/fi";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { LoginWithSmsModal } from "../components/LoginWithSmsModal";
import { CaptureUserDetailsModal } from "../components/CaptureUserDetailsModal";
import { useAuth } from "../contexts/AuthContext";

const Property = () => {
  const { isOpen: isSmsModalOpen, onOpen: onSmsModalOpen, onClose: onSmsModalClose } = useDisclosure();
  const { isOpen: isUserDetailsModalOpen, onOpen: onUserDetailsModalOpen, onClose: onUserDetailsModalClose } = useDisclosure();
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleLoginSuccess = () => {
    onSmsModalClose();
    if (user && (!user.fName || !user.lName || !user.email)) {
      onUserDetailsModalOpen();
    } else {
      window.location.href = "/p1";
    }
  };

  const handleUserDetailsSuccess = () => {
    onUserDetailsModalClose();
    window.location.href = "/p1";
  };

  const handleGetAccessClick = () => {
    if (isAuthenticated) {
      if (user && (!user.fName || !user.lName || !user.email)) {
        onUserDetailsModalOpen();
      } else {
        window.location.href = "/p1";
      }
    } else {
      onSmsModalOpen();
    }
  };

  return (
    <Box bg="#f7f7f7" minH="100vh">
      <NavbarWithCallToAction />
      
      <Box 
        bg="linear-gradient(135deg, #2c5282 0%, #39a169 100%)" 
        py={16} 
        color="white"
      >
        <Container maxW="container.md">
          <VStack spacing={6} textAlign="center">
            <Badge
              px={4}
              py={2}
              bg="white"
              color="#2c5282"
              borderRadius="full"
              fontSize="md"
              fontWeight="600"
            >
              FREE LIMITED-TIME ACCESS
            </Badge>
            
            <Heading size="2xl" lineHeight="1.2">
              Discover New Ways to Co-Manage Property and Invest with Blockchain Technology
            </Heading>
            
            <Text fontSize="xl" maxW="700px">
              Unlock exclusive content on innovative property investment strategies 
              powered by blockchain technology and collective decision-making
            </Text>
            
            <Button
              size="lg"
              colorScheme="green"
              rightIcon={<ChevronRightIcon />}
              onClick={handleGetAccessClick}
              px={8}
              py={7}
              fontSize="lg"
              boxShadow="lg"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "xl"
              }}
            >
              Get Free Podcast Access
            </Button>
          </VStack>
        </Container>
      </Box>

      <Container maxW="container.md" py={16}>
        <VStack spacing={12}>
          {/* Podcast Preview Section */}
          <Box 
            w="full" 
            bg="white" 
            p={8} 
            borderRadius="xl" 
            boxShadow="md"
            border="1px solid"
            borderColor="gray.100"
          >
            <VStack spacing={6} align="start">
              <Flex 
                w="full" 
                justify="center" 
                mb={4}
                bg="gray.100"
                minH="250px"
                borderRadius="lg"
                align="center"
                position="relative"
                overflow="hidden"
              >
                {/* Placeholder for podcast image/player */}
                <Text color="gray.500">Podcast Player Placeholder</Text>
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  bg="white"
                  w="80px"
                  h="80px"
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  cursor="pointer"
                  boxShadow="lg"
                  opacity="0.9"
                  _hover={{
                    opacity: 1,
                    transform: "translate(-50%, -50%) scale(1.05)"
                  }}
                  transition="all 0.2s"
                >
                  <Icon as={FiPlay} w={10} h={10} color="#39a169" />
                </Box>
              </Flex>
              
              <Heading size="lg">Free Podcast on New Ways to Co-Manage Property</Heading>
              
              <Text fontSize="md" color="gray.600">
                Host Sun Meng and special guest Tom discuss revolutionary approaches 
                to property investment, co-ownership models, and how blockchain 
                technology is changing the real estate landscape.
              </Text>

              <List spacing={3}>
                <ListItem>
                  <ListIcon as={CheckCircleIcon} color="green.500" />
                  Discover how to pool resources for property investment
                </ListItem>
                <ListItem>
                  <ListIcon as={CheckCircleIcon} color="green.500" />
                  Learn about blockchain-powered decision making
                </ListItem>
                <ListItem>
                  <ListIcon as={CheckCircleIcon} color="green.500" />
                  Understand SMSF options for property investments
                </ListItem>
                <ListItem>
                  <ListIcon as={CheckCircleIcon} color="green.500" />
                  Explore transparent governance models
                </ListItem>
              </List>
            </VStack>
          </Box>

          {/* Why Listen Section */}
          <Box w="full">
            <Heading size="xl" mb={8} textAlign="center">Why You Should Listen</Heading>
            
            <Flex 
              direction={{ base: "column", md: "row" }}
              wrap="wrap"
              justify="space-between"
              gap={8}
            >
              <Box 
                flex="1" 
                minW={{ base: "100%", md: "45%" }}
                bg="white"
                p={6}
                borderRadius="lg"
                boxShadow="sm"
                border="1px solid"
                borderColor="gray.100"
              >
                <Flex direction="column" h="full">
                  <Icon as={FiDollarSign} mb={4} w={10} h={10} color="#2c5282" />
                  <Heading size="md" mb={4}>Innovative Investment Models</Heading>
                  <Text flex="1">
                    Learn how fractional property ownership and blockchain technology 
                    enable average investors to access premium real estate opportunities 
                    with minimal capital.
                  </Text>
                </Flex>
              </Box>
              
              <Box 
                flex="1" 
                minW={{ base: "100%", md: "45%" }}
                bg="white"
                p={6}
                borderRadius="lg"
                boxShadow="sm"
                border="1px solid"
                borderColor="gray.100"
              >
                <Flex direction="column" h="full">
                  <Icon as={FiUsers} mb={4} w={10} h={10} color="#2c5282" />
                  <Heading size="md" mb={4}>Collective Decision Making</Heading>
                  <Text flex="1">
                    Discover transparent governance structures that empower all 
                    investors, regardless of investment size, to participate in 
                    property management decisions.
                  </Text>
                </Flex>
              </Box>
            </Flex>
          </Box>

          {/* Call to Action */}
          <Box
            w="full"
            bg="linear-gradient(135deg, rgba(51, 87, 134, 0.05) 0%, rgba(57, 161, 105, 0.1) 100%)"
            p={10}
            borderRadius="xl"
            border="1px solid"
            borderColor="rgba(57, 161, 105, 0.2)"
            textAlign="center"
          >
            <VStack spacing={6}>
              <Icon as={FiHeadphones} w={12} h={12} color="#39a169" />
              <Heading size="lg">Ready to Learn About Property Co-Ownership?</Heading>
              <Text fontSize="lg" maxW="600px">
                Enter your phone number to get instant access to our free podcast series 
                on blockchain-powered property investment
              </Text>
              <Button
                size="lg"
                colorScheme="green"
                rightIcon={<ChevronRightIcon />}
                onClick={handleGetAccessClick}
                px={8}
                py={7}
                fontSize="lg"
                boxShadow="md"
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "lg"
                }}
              >
                Get Free Podcast Access
              </Button>
            </VStack>
          </Box>
        </VStack>
      </Container>

      <LoginWithSmsModal 
        isOpen={isSmsModalOpen} 
        onClose={onSmsModalClose}
        onLoginSuccess={handleLoginSuccess}
      />
      {user && (
        <CaptureUserDetailsModal
          isOpen={isUserDetailsModalOpen}
          onClose={onUserDetailsModalClose}
          userId={user.id}
          onUpdateSuccess={handleUserDetailsSuccess}
          currentUserData={{
            fName: user.fName,
            lName: user.lName,
            email: user.email
          }}
        />
      )}
      
      <FooterWithFourColumns />
    </Box>
  );
};

export default Property; 